import React from 'react';
import {
  AlertsTabIcon,
  AviveHeartIcon,
  CallSharedIcon,
  ClockIcon,
  EPChat,
  EPTranscript,
  MapLayerIcon,
  MarkerIcon,
  PhoneIcon,
  UserIcon,
  W3wIcon,
  XCloseIcon,
} from '@rsos/assets/icons';
import { CommandHintFooter } from './CommandHintFooter';
import styles from './GlobalMapSearch.module.css';
import {
  Dropdown,
  EmptyState,
  ResultContent,
  ResultIcon,
  ResultItem,
  ResultLabel,
  ResultSublabel,
} from './GlobalMapSearch.styles';
import type { RecentSearchEntry } from './recentSearchEntry';
import type { PreparedItem } from './useGroupedResults';

interface RecentSearchesDropdownProps {
  dropdownRef: React.RefObject<HTMLDivElement>;
  recentSearches: RecentSearchEntry[];
  /**
   * Optional current query — when present we treat the visible list as
   * a *filtered* set of recents matching what the user is typing, and
   * adjust both the header copy and the row rendering (highlighted
   * substring) accordingly.
   */
  query?: string;
  cursor: number;
  onCursorChange: (idx: number) => void;
  onSelect: (entry: RecentSearchEntry) => void;
  onRemove: (entry: RecentSearchEntry) => void;
  onClearAll: () => void;
}

/**
 * Render `text` with the matched prefix recolored to the brand
 * accent. Pairs with the prefix-only filter in `GlobalMapSearch`
 * (recents are kept iff the entry's display text starts with `query`,
 * case-insensitive) — so the matched span is always exactly the
 * leading `query.length` characters of `text`. Falls back to plain
 * text when `query` is empty or, defensively, when `text` doesn't
 * actually start with it.
 *
 * Exported so the MapSearchDropdown's "Recent" section can render
 * recents identically to this dropdown without duplicating the
 * matching/styling rules.
 */
export const HighlightedRecent: React.FC<{ text: string; query: string }> = ({
  text,
  query,
}) => {
  const trimmed = query.trim();
  if (!trimmed) return <>{text}</>;
  const len = trimmed.length;
  if (text.slice(0, len).toLowerCase() !== trimmed.toLowerCase()) {
    return <>{text}</>;
  }
  return (
    <>
      <span className={styles.recentMatchToken}>{text.slice(0, len)}</span>
      {text.slice(len)}
    </>
  );
};

/**
 * Map a result-backed recent's persisted icon kind back to its React
 * glyph. Mirrors `ICON_BY_TYPE` in `MapSearchDropdown` — kept local so
 * each dropdown stays self-contained, since they consume different
 * subsets of the icon set.
 */
const RESULT_ICON_BY_TYPE: Record<PreparedItem['icon'], React.ReactNode> = {
  axon: <UserIcon width={16} height={16} />,
  place: <MarkerIcon width={16} height={16} />,
  what3words: <W3wIcon width={16} height={16} />,
  gis: <MapLayerIcon width={16} height={16} />,
  static: <MapLayerIcon width={16} height={16} />,
  dynamic: <MapLayerIcon width={16} height={16} />,
  call: <CallSharedIcon width={16} height={16} />,
  transcript: <EPTranscript width={16} height={16} />,
  message: <EPChat width={16} height={16} />,
  alert: <AlertsTabIcon width={16} height={16} />,
  // RAD-e rows: heart for MedicAlert / phone for ADR — mirrors the
  // glyph choices in `MapSearchDropdown.ICON_BY_TYPE`.
  'rade-maf': <AviveHeartIcon width={16} height={16} />,
  'rade-adr': <PhoneIcon width={16} height={16} />,
};

/**
 * Render the leading icon for a recent entry. Result-backed entries
 * reuse the same icon they had in the live results dropdown (so a
 * recent call shows the call icon, a recent hydrant shows the
 * hydrant silhouette, etc.). Plain query entries get the generic
 * clock icon, which matches the pre-existing visual.
 */
const RecentEntryIcon: React.FC<{ entry: RecentSearchEntry }> = ({ entry }) => {
  if (!entry.result) return <ClockIcon width={16} height={16} />;
  if (entry.result.iconUrl) {
    return (
      <span
        className={styles.categoryIcon}
        style={
          {
            '--category-icon-url': `url(${entry.result.iconUrl})`,
          } as React.CSSProperties
        }
        aria-hidden
      />
    );
  }
  return RESULT_ICON_BY_TYPE[entry.result.icon];
};

/**
 * Pre-search dropdown surface — surfaces the user's recent global-map search
 * queries (and clicked records) so they can re-run / re-open one with a
 * single click. When there's no history yet we render a friendly nudge
 * instead of an empty box so the focus state always shows *something*.
 *
 * Pure presentational; all state and handlers come from `GlobalMapSearch`.
 */
const RecentSearchesDropdown: React.FC<RecentSearchesDropdownProps> = ({
  dropdownRef,
  recentSearches,
  query = '',
  cursor,
  onCursorChange,
  onSelect,
  onRemove,
  onClearAll,
}) => {
  const hasHistory = recentSearches.length > 0;
  const isFiltering = query.trim().length > 0;

  // Header label encodes the three states: a typed query that matched
  // ("Matching recents"), an untouched bar with history ("Recent
  // searches"), and a fresh user with neither ("Search the map").
  let headerLabel: string;
  if (isFiltering) headerLabel = 'Matching recents';
  else if (hasHistory) headerLabel = 'Recent searches';
  else headerLabel = 'Search the map';

  return (
    <Dropdown
      ref={dropdownRef}
      className={styles.recentsDropdown}
      onMouseDown={e => e.preventDefault()}
    >
      <div className={styles.recentHeader}>
        <span className={styles.recentHeaderLabel}>{headerLabel}</span>
        {hasHistory && !isFiltering && (
          <button
            type="button"
            className={styles.recentClearAll}
            onClick={onClearAll}
          >
            Clear all
          </button>
        )}
      </div>

      {hasHistory ? (
        recentSearches.map((entry, idx) => {
          const isActive = cursor === idx;
          // Result-backed entries lead with the saved label (the
          // record's name/address/etc.) so the user is reminded of
          // *what* they opened, not just *what* they typed. Plain
          // query entries keep the original behavior.
          const primaryText = entry.result?.label ?? entry.query;
          const sublabel = entry.result?.sublabel;
          const removeLabel = entry.result
            ? `Remove "${entry.result.label}" from recent searches`
            : `Remove "${entry.query}" from recent searches`;
          return (
            <ResultItem
              key={entry.key}
              data-result-index={idx}
              isActive={isActive}
              onClick={() => onSelect(entry)}
              onMouseEnter={() => onCursorChange(idx)}
            >
              <ResultIcon>
                <RecentEntryIcon entry={entry} />
              </ResultIcon>
              <ResultContent>
                <ResultLabel>
                  <HighlightedRecent text={primaryText} query={query} />
                </ResultLabel>
                {sublabel && <ResultSublabel>{sublabel}</ResultSublabel>}
              </ResultContent>
              <button
                type="button"
                className={styles.recentRemoveBtn}
                aria-label={removeLabel}
                onClick={e => {
                  // Stop the row's onClick from re-running the search
                  // when the user only meant to delete the entry.
                  e.stopPropagation();
                  onRemove(entry);
                }}
              >
                <XCloseIcon width={12} height={12} />
              </button>
            </ResultItem>
          );
        })
      ) : (
        <EmptyState>
          {isFiltering
            ? 'No recent searches match.'
            : 'Start searching to see your recent queries here.'}
        </EmptyState>
      )}

      <CommandHintFooter />
    </Dropdown>
  );
};

export default RecentSearchesDropdown;
